import { useState } from 'react';
import { leadsAPI } from '@/lib/api';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/* Importiert Kontakte aus einer CSV/Excel-Datei — z. B. ein Mitgliederexport
   aus Octiv Fitness, Magicline, EasyFitness u. Ä. Zeilen mit erkannten
   Mitgliedschafts-/Vertragsdaten (Tarif, Beitrag, Vertragsbeginn) werden im
   Backend automatisch als bereits gewonnene Mitglieder mit aktiver
   Mitgliedschaft angelegt, statt als neue, unbearbeitete Leads. */

export default function ImportDialog({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'done'
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setStep('upload');
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setPreview(null);
    setStep('upload');
    setResult(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await leadsAPI.previewCSV(formData);
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Vorschau fehlgeschlagen';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await leadsAPI.importCSV(formData);
      setResult(res.data);
      setStep('done');
      onSuccess();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Import fehlgeschlagen';
      if (errorDetail) {
        errorMessage = typeof errorDetail === 'string' ? errorDetail : Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : errorDetail.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === 'upload' && 'CSV / Excel Import'}
            {step === 'preview' && 'Vorschau prüfen'}
            {step === 'done' && 'Import abgeschlossen'}
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="rounded-lg border-2 border-dashed border-muted-foreground/25 p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground mb-3">CSV- oder Excel-Datei aus deinem Gym-System hochladen (z. B. Octiv Fitness, Magicline)</p>
              <Input type="file" accept=".csv,.xlsx" onChange={handleFileChange} className="max-w-xs mx-auto" data-testid="csv-file-input" />
            </div>
            {file && (
              <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                  {file.name.endsWith('.xlsx') ? 'XLS' : 'CSV'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Erkannte Spalten (deutsch & englisch):</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                <span>• Name / Vorname + Nachname</span>
                <span>• Email / E-Mail</span>
                <span>• Telefon / Handy / Phone</span>
                <span>• Notizen / Bemerkung</span>
                <span>• Mitgliedschaft / Tarif / Plan / Package(s)</span>
                <span>• Monatsbeitrag / Preis / Member rate</span>
                <span>• Vertragsbeginn / Start</span>
                <span>• Vertragsende / Ende</span>
                <span>• Status (aktiv/gekündigt)</span>
                <span>• Adresse, Ort, Geburtstag</span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Zeilen mit Mitgliedschafts- oder Vertragsdaten werden als bereits gewonnene Mitglieder
                mit aktiver Mitgliedschaft angelegt — nicht als neue Leads.
              </p>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="secondary" className="text-xs">{preview.total_rows} Zeilen gefunden</Badge>
              {preview.has_membership_data && (
                <Badge className="text-xs bg-green-600 text-white">Mitgliedschaftsdaten erkannt</Badge>
              )}
              <Badge variant="outline" className="text-xs">{Object.keys(preview.mapped_columns).length} Spalten zugeordnet</Badge>
            </div>

            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">Spaltenzuordnung:</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(preview.mapped_columns).map(([key, col]) => (
                  <span key={key} className="text-xs bg-muted px-2 py-0.5 rounded">
                    {col} → <span className="font-semibold">{key}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">E-Mail</th>
                    <th className="px-3 py-2 text-left font-semibold">Telefon</th>
                    {preview.has_membership_data && (
                      <>
                        <th className="px-3 py-2 text-left font-semibold">Mitgliedschaft</th>
                        <th className="px-3 py-2 text-left font-semibold">Beitrag</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={`preview-${row.name || ''}-${row.email || ''}-${i}`} className="border-b last:border-0">
                      <td className="px-3 py-1.5 font-medium">{row.name || <span className="text-destructive">—</span>}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{row.email || '—'}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{row.phone || '—'}</td>
                      {preview.has_membership_data && (
                        <>
                          <td className="px-3 py-1.5">{row.membership || '—'}</td>
                          <td className="px-3 py-1.5">{row.monthly_price ? `${row.monthly_price} €` : '—'}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.total_rows > 5 && (
                <p className="text-[10px] text-muted-foreground text-center py-1.5 bg-muted/30">
                  ... und {preview.total_rows - 5} weitere Zeilen
                </p>
              )}
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <p className="font-semibold text-green-800 dark:text-green-200 text-lg">{result.imported} Mitglieder importiert</p>
              {result.memberships_created > 0 && (
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">{result.memberships_created} Mitgliedschaften erstellt</p>
              )}
              {result.chats_zugeordnet > 0 && (
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">{result.chats_zugeordnet} WhatsApp-Chats im Posteingang zugeordnet</p>
              )}
              {result.duplicates > 0 && (
                <p className="text-xs text-amber-600 mt-2">{result.duplicates} bereits vorhanden – übersprungen (Dubletten-Schutz)</p>
              )}
              {(result.skipped - (result.duplicates || 0)) > 0 && (
                <p className="text-xs text-amber-600 mt-1">{result.skipped - (result.duplicates || 0)} Zeilen übersprungen (Name fehlend)</p>
              )}
            </div>
            {result.detail && (
              <p className="text-xs text-muted-foreground bg-muted/50 rounded p-2">{result.detail}</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={handleClose}>Abbrechen</Button>
              <Button onClick={handlePreview} disabled={!file || previewing} data-testid="preview-csv-btn">
                {previewing ? 'Wird geladen...' : 'Vorschau anzeigen'}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>Zurück</Button>
              <Button onClick={handleImport} disabled={importing} data-testid="import-submit-btn">
                {importing ? 'Wird importiert...' : `${preview?.total_rows || 0} Einträge importieren`}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} data-testid="import-close-btn">Schließen</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
